import type { ReactNode, RefObject } from 'react';
import type { Chart as ChartJS } from 'chart.js';
import classNames from 'classnames';

import SkeletonBox from '@/components/atoms/SkeletonBox/SkeletonBox';
import BaseChart from '@/components/charts/BaseChart';
import { ChartErrorState } from '@/components/charts/ChartErrorState';
import { hasPlottableValue } from '@/components/charts/hasPlottableValue';
import EmptyState from '@/components/molecules/EmptyState';

import { DatasetLegend, type LegendItem } from '../legend';
import { metricBarLabelsPlugin } from '../plugins/barLabelsPlugin';

import type { MetricChartData, MetricChartOptions } from './optionTypes';
import type { MetricChartKind } from './types';

import styles from './MetricChart.module.scss';

const DEFAULT_CHART_HEIGHT = 260;

/** Inert on a bar chart whose options carry no `metricBarLabels`. */
const BAR_PLUGINS = [metricBarLabelsPlugin];

export interface MetricChartProps<TKind extends MetricChartKind> {
    kind: TKind;
    data: MetricChartData<TKind>;
    options: MetricChartOptions<TKind>;
    legendItems: LegendItem[];
    visibleSeries: Set<number>;
    onToggleSeries: (index: number) => void;
    chartRef: RefObject<ChartJS<TKind> | null>;
    /** Plot height in px, or `'fill'` to take the height of a sized parent. */
    height?: number | 'fill';
    /**
     * Sizes the plot by width instead of `height`, for a chart in a grid column that should
     * keep its proportions at every width (and in a PDF export).
     */
    aspectRatio?: number;
    isLoading?: boolean;
    /** Rendered instead of the chart when set. */
    error?: unknown;
    /** Offers Try again in the error state. */
    onRetry?: () => void;
    isRetrying?: boolean;
    /** Extra content beside the legend — e.g. a right-axis selector. */
    legendRight?: ReactNode;
    /**
     * `centered` lines the legend and `legendRight` up on their centres, 8px apart, with 16px
     * to the plot: the row the Behavior charts have always had. The default tops them,
     * 16px apart, with 12px to the plot.
     */
    legendRowLayout?: 'default' | 'centered';
    /**
     * Rendered in place of the dataset legend, for a chart whose series visibility lives
     * outside `useMetricChart` (the Compare page's cage legend).
     */
    legend?: ReactNode;
    /**
     * Shown in place of the chart when every series is empty.
     *
     * Set this rather than hiding the panel when a source is wired up but silent: an
     * absent panel is indistinguishable from an unimplemented one, and it also removes
     * whatever data-quality indicator the panel's header carries. Self-guarding — the
     * chart still renders as soon as any value arrives, so a caller cannot leave a
     * stale empty state behind.
     */
    noDataMessage?: string;
    /**
     * Already translated. Names the chart for screen readers, with its series. Left out, the
     * chart takes the title of the card or panel it sits in (`ChartTitleProvider`).
     */
    title?: string;
}

/**
 * Renders a metric chart with its interactive legend and its loading and error
 * states. Deliberately owns no card chrome — no title, no header actions — so it
 * stays free of any dependency on page-level layout components.
 *
 * Feed it the result of `useMetricChart`. Callers that want the standard card
 * look wrap this in their own container alongside `ChartHeaderActions`.
 *
 * `error` is rendered rather than ignored: the Behavior chart cards thread an
 * error down from the query and then never read it, so a failed request shows an
 * empty chart with no indication that anything went wrong.
 */
export default function MetricChart<TKind extends MetricChartKind>(props: MetricChartProps<TKind>) {
    const {
        kind,
        data,
        options,
        legendItems,
        visibleSeries,
        onToggleSeries,
        chartRef,
        height = DEFAULT_CHART_HEIGHT,
        aspectRatio,
        isLoading = false,
        error,
        onRetry,
        isRetrying,
        legendRight,
        legendRowLayout = 'default',
        legend,
        noDataMessage,
        title,
    } = props;

    const fill = height === 'fill';
    const areaStyle = aspectRatio !== undefined ? { aspectRatio } : fill ? undefined : { height };
    // A plot sized by its width or its parent has no height of its own to hand the skeleton
    // and the states, so they fill the plot area instead.
    const stateHeight = aspectRatio !== undefined || fill ? '100%' : height;

    // Error and loading both outrank it: a failed or pending request is not "no data".
    const showEmptyState =
        !error && !isLoading && noDataMessage !== undefined && !hasPlottableValue(data);

    // The legend stays mounted through the empty state. `data` holds only the VISIBLE
    // series, so hiding the last series that has values engages the empty state — and
    // unmounting the legend there would strand it hidden with no control left to restore
    // it, and would take `legendRight` (a control, not a legend, for some callers) with
    // it. Current callers pass a single `showInLegend: false` series and render no legend
    // in any state; this keeps the prop safe for the multi-series callers next in line.

    function renderChart() {
        if (error) {
            return (
                <ChartErrorState height={stateHeight} onRetry={onRetry} isRetrying={isRetrying} />
            );
        }

        if (isLoading) {
            return <SkeletonBox width="100%" height={stateHeight} borderRadius={8} />;
        }

        if (showEmptyState) {
            return (
                <EmptyState
                    title={noDataMessage}
                    appearance="quiet"
                    live="polite"
                    height={stateHeight}
                />
            );
        }

        // `kind` is the runtime discriminant for the generic `TKind`, which
        // TypeScript cannot narrow on its own. The casts below are guaranteed by
        // that check and are the only place this indirection is needed.
        if (kind === 'bar') {
            return (
                <BaseChart
                    type="bar"
                    data={data as MetricChartData<'bar'>}
                    options={options as MetricChartOptions<'bar'>}
                    maintainAspectRatio={false}
                    chartRef={chartRef as RefObject<ChartJS<'bar'>>}
                    plugins={BAR_PLUGINS}
                    title={title}
                />
            );
        }

        return (
            <BaseChart
                type="line"
                data={data as MetricChartData<'line'>}
                options={options as MetricChartOptions<'line'>}
                maintainAspectRatio={false}
                chartRef={chartRef as RefObject<ChartJS<'line'>>}
                title={title}
            />
        );
    }

    return (
        <div className={classNames(styles.chartContainer, { [styles.fill]: fill })}>
            {(legend || legendItems.length > 0 || legendRight) && (
                <div
                    className={classNames(styles.legendRow, {
                        [styles.legendRowCentered]: legendRowLayout === 'centered',
                    })}>
                    {legend ?? (
                        <DatasetLegend
                            legendItems={legendItems}
                            visibleDatasets={visibleSeries}
                            onToggleDataset={onToggleSeries}
                        />
                    )}
                    {legendRight}
                </div>
            )}
            <div className={styles.chartArea} style={areaStyle}>
                {renderChart()}
            </div>
        </div>
    );
}
